import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Checkbox,
    FormControlLabel,
    useTheme,
    useMediaQuery,
    Stack,
    Chip
} from '@mui/material';
import { ICalendarEntry } from '../Interfaces/ICalendarEntry';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PlaceIcon from '@mui/icons-material/Place';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarBorderRoundedIcon from '@mui/icons-material/StarBorderRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {checkPermission} from "../utils/permission";
import { useAlert } from '../core/AlertContext';
import { useGetTags } from '../hooks/tags';
import TagChip from '../core/TagChip';
import EventRsvpSection from './components/EventRsvpSection';
import EventCommentSection from './components/EventCommentSection';
import MediaStrip from './components/MediaStrip';
import EventMoreOptionsDialog from './components/EventMoreOptionsDialog';
import { EVENT_DIALOG_MAX_WIDTH, EVENT_DIALOG_PAPER_SX } from './dialogLayout';
import AuditInfoDialog from '../core/AuditInfoDialog';
import { fieldSx, primaryButtonSx, secondaryButtonSx, GREEN_800 } from '../core/pvtaStyles';

// Hilfsfunktion: Formatiert ein Date‑Objekt als "YYYY-MM-DD"
const formatDateForInput = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Hilfsfunktion: Formatiert ein Date‑Objekt als "HH:mm"
const formatTimeForInput = (date: Date): string => {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
};

// Angepasste parseLocalDateTime: Wenn der String ein "T" enthält (ISO‑Format),
// wird direkt new Date(dateStr) verwendet. Andernfalls wird das Format "YYYY-MM-DD HH:mm:ss.ffffff" angenommen.
const parseLocalDateTime = (dateStr: string): Date => {
    if (dateStr.includes('T')) {
        return new Date(dateStr);
    } else {
        const [datePart, timePart] = dateStr.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        let hour = 0, minute = 0, second = 0;
        if (timePart) {
            const timeParts = timePart.split(':');
            hour = Number(timeParts[0]);
            minute = Number(timeParts[1]);
            second = Number(timeParts[2].split('.')[0]);
        }
        return new Date(year, month - 1, day, hour, minute, second);
    }
};

interface CalendarEventEditProps {
    event: ICalendarEntry;
    onClose: () => void;
    onSave: (updatedEvent: ICalendarEntry) => void;
    onDelete: (id: number) => void;
    onToggleFavorite?: (eventId: number, nextFavoriteState: boolean) => void;
    isFavoritePending?: boolean;
}

const CalendarEventEdit: React.FC<CalendarEventEditProps> = ({
    event,
    onClose,
    onSave,
    onDelete,
    onToggleFavorite,
    isFavoritePending = false,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { showAlert } = useAlert();
    const [auditDialogOpen, setAuditDialogOpen] = useState(false);
    const [moreOptionsOpen, setMoreOptionsOpen] = useState(false);

    // Lokaler State für editierbare Felder
    const [title, setTitle] = useState<string>(event.title);
    const [description, setDescription] = useState<string>(event.description || '');
    const [location, setLocation] = useState<string>(event.location || '');
    const [allDay, setAllDay] = useState<boolean>(event.all_day);

    // Datum im Format "YYYY-MM-DD" mithilfe von parseLocalDateTime
    const [startDate, setStartDate] = useState<string>(
        event.start_time ? formatDateForInput(parseLocalDateTime(event.start_time)) : ''
    );
    const [endDate, setEndDate] = useState<string>(
        event.end_time ? formatDateForInput(parseLocalDateTime(event.end_time)) : ''
    );

    // Zeit im Format "HH:mm" – mithilfe von parseLocalDateTime
    const [startTime, setStartTime] = useState<string>(
        event.start_time ? formatTimeForInput(parseLocalDateTime(event.start_time)) : ''
    );
    const [endTime, setEndTime] = useState<string>(
        event.end_time ? formatTimeForInput(parseLocalDateTime(event.end_time)) : ''
    );

    const calendarType = event.calendar_type;

    const canEdit = checkPermission(1, 2);
    const { tags: availableTags } = useGetTags();
    const [selectedTagIds, setSelectedTagIds] = useState<number[]>((event.tags ?? []).map((tag) => tag.id));
    const toggleTag = (tagId: number): void => {
        setSelectedTagIds((previous) =>
            previous.includes(tagId) ? previous.filter((id) => id !== tagId) : [...previous, tagId]
        );
    };
    const selectedTags = (event.tags ?? []).filter((tag) => selectedTagIds.includes(tag.id));

    const formatMetaDate = (dateString: string): string => (
        parseLocalDateTime(dateString).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        })
    );

    const formatMetaTime = (dateString: string): string => (
        parseLocalDateTime(dateString).toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
        })
    );

    const metaDateLabel = event.start_time && event.end_time
        ? `${formatMetaDate(event.start_time)}${formatMetaDate(event.start_time) !== formatMetaDate(event.end_time) ? ` - ${formatMetaDate(event.end_time)}` : ''}`
        : '';

    const metaTimeLabel = allDay
        ? 'Ganztägig'
        : (event.start_time && event.end_time ? `${formatMetaTime(event.start_time)} - ${formatMetaTime(event.end_time)}` : '');

    const handleSave = () => {
        // Titel als Pflichtfeld prüfen
        if (!title.trim()) {
            showAlert({ severity: 'warning', text: 'Bitte geben Sie einen Titel ein.' });
            return;
        }

        let updatedStart: string;
        let updatedEnd: string;
        if (allDay) {
            // Bei ganztägigen Events nur das Datum übernehmen
            updatedStart = startDate;
            updatedEnd = endDate;
        } else {
            // Datum und Zeit werden als Zeichenkette kombiniert – ohne Zeitzonen-Umrechnung.
            updatedStart = startDate && startTime ? `${startDate} ${startTime}:00` : '';
            updatedEnd = endDate && endTime ? `${endDate} ${endTime}:00` : '';
        }

        const updatedEvent: ICalendarEntry = {
            ...event,
            title,
            description: description.trim() === '' ? null : description,
            location: location.trim() === '' ? null : location,
            all_day: allDay,
            start_time: updatedStart,
            end_time: updatedEnd,
            calendar_type: calendarType,
            tag_ids: selectedTagIds,
        };
        onSave(updatedEvent);
    };

    return (
        <Dialog
            open
            onClose={onClose}
            fullWidth
            fullScreen={isMobile}
            maxWidth={EVENT_DIALOG_MAX_WIDTH}
            PaperProps={EVENT_DIALOG_PAPER_SX}
        >
            <DialogTitle sx={{ pb: 1.5, pt: { xs: 2, sm: 2.5 }, px: { xs: 2, sm: 3 } }}>
                <Stack spacing={1.25}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            justifyContent: 'space-between',
                            gap: 1.5,
                        }}
                    >
                        <Box sx={{ minWidth: 0 }}>
                            <Typography
                                variant={isMobile ? 'h6' : 'h5'}
                                sx={{
                                    fontWeight: 800,
                                    lineHeight: 1.2,
                                    overflowWrap: 'anywhere',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {title || (event.id ? 'Veranstaltung bearbeiten' : 'Neue Veranstaltung erstellen')}
                            </Typography>
                        </Box>

                        <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                            {event.id && event.calendar_type === 'event' && onToggleFavorite && (
                                <IconButton
                                    aria-label={event.is_favorite ? 'aus Favoriten entfernen' : 'zu Favoriten hinzufügen'}
                                    onClick={() => onToggleFavorite(event.id as number, !Boolean(event.is_favorite))}
                                    disabled={isFavoritePending}
                                    sx={{
                                        minHeight: 44,
                                        minWidth: 44,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        color: event.is_favorite ? 'var(--pvta-favorite)' : 'inherit',
                                    }}
                                >
                                    {event.is_favorite ? <StarRoundedIcon /> : <StarBorderRoundedIcon />}
                                </IconButton>
                            )}
                            {event.id && event.calendar_type === 'event' && canEdit && (
                                <IconButton
                                    aria-label="Weitere Optionen"
                                    onClick={() => setMoreOptionsOpen(true)}
                                    sx={{
                                        minHeight: 44,
                                        minWidth: 44,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <MoreVertIcon />
                                </IconButton>
                            )}
                            {event.id && (
                                <IconButton
                                    aria-label="weitere Informationen"
                                    onClick={() => setAuditDialogOpen(true)}
                                    sx={{
                                        minHeight: 44,
                                        minWidth: 44,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <InfoOutlinedIcon />
                                </IconButton>
                            )}
                            {(event.id && checkPermission(1,2)) && (
                                <IconButton
                                    aria-label="delete"
                                    onClick={() => onDelete(event.id as number)}
                                    sx={{
                                        minHeight: 44,
                                        minWidth: 44,
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            )}
                            <IconButton
                                aria-label="close"
                                onClick={onClose}
                                sx={{
                                    minHeight: 44,
                                    minWidth: 44,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Stack>
                    </Box>

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {metaDateLabel && (
                            <Chip
                                icon={<EventIcon fontSize="small" />}
                                label={metaDateLabel}
                                size="small"
                                sx={{ maxWidth: '100%' }}
                            />
                        )}
                        {metaTimeLabel && (
                            <Chip
                                icon={<AccessTimeIcon fontSize="small" />}
                                label={metaTimeLabel}
                                size="small"
                            />
                        )}
                        {location.trim() && (
                            <Chip
                                icon={<PlaceIcon fontSize="small" />}
                                label={location}
                                size="small"
                                sx={{
                                    maxWidth: '100%',
                                    '& .MuiChip-label': {
                                        overflowWrap: 'anywhere',
                                        wordBreak: 'break-word',
                                    },
                                }}
                            />
                        )}
                    </Stack>
                </Stack>
            </DialogTitle>
            <DialogContent dividers sx={{ pt: 2, px: { xs: 2, sm: 3 } }}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    {/* Titel */}
                    <Grid item xs={12}>
                        <TextField
                            label="Titel"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            fullWidth
                            required
                            error={!title.trim()}
                            helperText={!title.trim() ? "Titel ist ein Pflichtfeld." : ""}
                            size="small"
                            sx={{ ...fieldSx }}
                            slotProps={{
                                input: {
                                    readOnly: !checkPermission(1,2),
                                },
                            }}
                        />
                    </Grid>
                    {/* Beschreibung */}
                    <Grid item xs={12}>
                        <TextField
                            label="Beschreibung"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            size="small"
                            sx={{ ...fieldSx }}
                            slotProps={{
                                input: {
                                    readOnly: !checkPermission(1,2),
                                },
                            }}
                        />
                    </Grid>
                    {/* Ort */}
                    <Grid item xs={12}>
                        <TextField
                            label="Ort"
                            value={location}
                            onChange={(e) => setLocation(e.target.value)}
                            fullWidth
                            size="small"
                            sx={{ ...fieldSx }}
                            slotProps={{
                                input: {
                                    readOnly: !checkPermission(1,2),
                                },
                            }}
                        />
                    </Grid>
                    {/* Tags */}
                    {(canEdit ? availableTags.length > 0 : selectedTags.length > 0) && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: GREEN_800 }}>
                                Tags
                            </Typography>
                            {canEdit ? (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {availableTags.map((tag) => {
                                        const active = selectedTagIds.includes(tag.id);
                                        return (
                                            <Chip
                                                key={tag.id}
                                                label={tag.name}
                                                onClick={() => toggleTag(tag.id)}
                                                sx={{
                                                    borderRadius: 999,
                                                    fontWeight: 600,
                                                    cursor: 'pointer',
                                                    backgroundColor: active ? tag.color : 'transparent',
                                                    color: active ? '#ffffff' : 'inherit',
                                                    border: '1px solid',
                                                    borderColor: active ? tag.color : 'divider',
                                                }}
                                            />
                                        );
                                    })}
                                </Box>
                            ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {selectedTags.map((tag) => (
                                        <TagChip key={tag.id} tag={tag} />
                                    ))}
                                </Box>
                            )}
                        </Grid>
                    )}
                    {/* Startdatum + Enddatum nebeneinander */}
                    <Grid item xs={6}>
                        <TextField
                            type="date"
                            label="Startdatum"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            fullWidth
                            size="small"
                            sx={{ ...fieldSx }}
                            InputLabelProps={{ shrink: true }}
                            slotProps={{
                                input: {
                                    readOnly: !checkPermission(1,2),
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            type="date"
                            label="Enddatum"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            fullWidth
                            size="small"
                            sx={{ ...fieldSx }}
                            InputLabelProps={{ shrink: true }}
                            slotProps={{
                                input: {
                                    readOnly: !checkPermission(1,2),
                                },
                            }}
                        />
                    </Grid>
                    {/* Startzeit + Endzeit nebeneinander */}
                    {!allDay && (
                        <>
                            <Grid item xs={6}>
                                <TextField
                                    type="time"
                                    label="Startzeit"
                                    value={startTime}
                                    onChange={(e) => setStartTime(e.target.value)}
                                    fullWidth
                                    size="small"
                                    sx={{ ...fieldSx }}
                                    InputLabelProps={{ shrink: true }}
                                    slotProps={{
                                        input: {
                                            readOnly: !checkPermission(1,2),
                                        },
                                    }}
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    type="time"
                                    label="Endzeit"
                                    value={endTime}
                                    onChange={(e) => setEndTime(e.target.value)}
                                    fullWidth
                                    size="small"
                                    sx={{ ...fieldSx }}
                                    InputLabelProps={{ shrink: true }}
                                    slotProps={{
                                        input: {
                                            readOnly: !checkPermission(1,2),
                                        },
                                    }}
                                />
                            </Grid>
                        </>
                    )}
                    {/* Ganztägiges Event */}
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    disabled={!checkPermission(1,2)}
                                    checked={allDay}
                                    onChange={(e) => setAllDay(e.target.checked)}
                                />
                            }
                            label="Ganztägiges Event"
                        />
                    </Grid>

                    {event.id && event.calendar_type === 'event' && (
                        <Grid item xs={12}>
                            <EventRsvpSection eventId={event.id} />
                        </Grid>
                    )}

                    {event.id && event.calendar_type === 'event' && (
                        <Grid item xs={12}>
                            <MediaStrip entryId={event.id} />
                        </Grid>
                    )}

                    {event.id && event.calendar_type === 'event' && (
                        <Grid item xs={12}>
                            <EventCommentSection eventId={event.id} />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
                <Button onClick={onClose} sx={{ ...secondaryButtonSx }}>Abbrechen</Button>
                {checkPermission(1,2) &&
                    <Button onClick={handleSave} sx={{ ...primaryButtonSx }}>
                        Speichern
                    </Button>
                }
            </DialogActions>
            <AuditInfoDialog
                open={auditDialogOpen}
                onClose={() => setAuditDialogOpen(false)}
                title="Veranstaltungsinformationen"
                createdBy={event.created_by}
                createdAt={event.created_at}
                updatedBy={event.updated_by}
                updatedAt={event.updated_at}
                deletedBy={event.deleted_by}
                deletedAt={event.deleted_at}
            />
            {event.id && event.calendar_type === 'event' && canEdit && (
                <EventMoreOptionsDialog
                    open={moreOptionsOpen}
                    eventId={event.id}
                    eventTitle={event.title}
                    onClose={() => setMoreOptionsOpen(false)}
                />
            )}
        </Dialog>
    );
};

export default CalendarEventEdit;
